import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Node, Slice, Fragment } from '@tiptap/pm/model';
import { relativePositionToAbsolutePosition, ySyncPluginKey } from 'y-prosemirror';

export interface CommentOptions {
  HTMLAttributes?: Record<string, any>;
  emitter?: any;
  getTextNodeFromPoint?: any;
}

const commentMarkName = 'comment';

const createCommentMark = (comment: string, schema: any, resolved: string = '') => {
  return schema.marks.comment.create({
    ...(comment && { comment }),
    ...(resolved && { resolved }),
  });
};

const markTypeIsComment = (mark: any = {}) => {
  return mark?.type?.name === commentMarkName;
};

const getMarksWithoutComment = (node: any) => {
  return node.marks.filter((mark: any) => !markTypeIsComment(mark));
};

const findAllCommentMarks = (doc: any) => {
  const commentMarks: any = [];
  // find all comment marks in document
  doc.descendants((node: Node, pos: number) => {
    const marks = getCommentMarks(node);
    if (marks.length) {
      commentMarks.push(
        ...marks.map((mark: any) => ({ ...mark, from: pos, to: pos + node.nodeSize }))
      );
    }
  });
  return commentMarks;
};

const getCommentMarks = (node: any = {}) => {
  return node?.marks?.filter?.(markTypeIsComment);
};

const getCommentMark = (node: any = {}) => {
  return node?.marks?.find?.(markTypeIsComment);
};

const createNodeWithoutCommentChildren = (node: any = {}) => {
  return (
    node.type?.create?.(
      node.attrs,
      node.content.content.map(removeMarks),
      getMarksWithoutComment(node)
    ) ?? node
  );
};

// recursively remove all comment marks from the pasted content
const removeMarks = (node: any): Fragment => {
  if (node.isText) {
    const marksWithoutComment = getMarksWithoutComment(node);
    return node.mark(marksWithoutComment);
  } else if (node.isBlock) {
    return createNodeWithoutCommentChildren(node);
  } else if (node instanceof Fragment) {
    return Fragment.fromArray(
      // @ts-ignore
      node.content.map(removeMarks)
    );
  } else {
    return createNodeWithoutCommentChildren(node);
  }
};

// Check if the content contains any comment marks
const hasCommentMarkInFragment = (fragment: Fragment): boolean => {
  let found = false;
  fragment.forEach((node: Node) => {
    if (found) return;
    if (node.marks.some(markTypeIsComment)) {
      found = true;
    } else if (node.content) {
      found = hasCommentMarkInFragment(node.content);
    }
  });
  return found;
};

const updateComment = (commentId: string, isResolve: boolean) => {
  return ({ state, tr }: { state: any; tr: any }): boolean => {
    const { schema } = state;

    // Find all comment marks in the document.
    const commentMarks: any = findAllCommentMarks(state.doc);

    // if there are no comment marks in document return false
    if (!commentMarks.length) return false;

    commentMarks.forEach((mark: any) => {
      const { comment, resolved } = mark.attrs;

      // Split the comment and resolved string into arrays.
      const commentIds = comment?.split?.(' ') ?? [];
      const resolvedCommentIds = resolved?.split?.(' ') ?? [];

      // Find the array that we want to update.
      const sourceIds = isResolve ? commentIds : resolvedCommentIds;
      const targetIds = isResolve ? resolvedCommentIds : commentIds;

      if (sourceIds.includes(commentId)) {
        // Remove the commentId from the source array.
        const newSourceIds = sourceIds.filter((id: string) => id !== commentId).join(' ');

        // Add the commentId to the target array.
        targetIds.unshift(commentId);
        const newTargetIds = targetIds.join(' ');

        const commentIds = isResolve ? newSourceIds : newTargetIds;
        const resolvedIds = isResolve ? newTargetIds : newSourceIds;

        // Create the new comment mark.
        const newCommentMark = createCommentMark(commentIds, schema, resolvedIds);

        // Add the new comment mark to the transaction.
        tr.addMark(mark.from, mark.to, newCommentMark);
      }
    });
    return true;
  };
};

export {
  createCommentMark,
  markTypeIsComment,
  getMarksWithoutComment,
  getCommentMarks,
  getCommentMark,
  createNodeWithoutCommentChildren,
  removeMarks,
  hasCommentMarkInFragment,
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: {
      /**
       * Set a comment mark
       */
      setComment: (comment: string) => ReturnType;
      /**
       * Unset a comment mark
       */
      removeComment: (commentId: string) => ReturnType;
      /**
       * Resolve a comment mark
       */
      resolveComment: (commentId: string) => ReturnType;
      /**
       * Unresolve a comment mark
       */
      unresolveComment: (commentId: string) => ReturnType;
    };
  }
}

export default Mark.create<CommentOptions>({
  name: commentMarkName,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  inclusive: false,

  addAttributes() {
    return {
      comment: {
        default: null,
        parseHTML: (el) => (el as HTMLSpanElement).getAttribute('data-comment-ids'),
        renderHTML: (attrs) => ({ 'data-comment-ids': attrs.comment }),
      },
      resolved: {
        default: null,
        parseHTML: (el) => (el as HTMLSpanElement).getAttribute('data-resolved-ids'),
        renderHTML: (attrs) => ({ 'data-resolved-ids': attrs.resolved }),
      },
    };
  },

  addStorage() {
    return {
      activeCommentId: null,
      cut: false,
      inlineCommentActive: false,
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-comment-ids]',
        getAttrs: (el) =>
          !!(el as HTMLSpanElement).getAttribute('data-comment-ids')?.trim() && null,
      },
      {
        tag: 'span[data-resolved-ids]',
        getAttrs: (el) =>
          !!(el as HTMLSpanElement).getAttribute('data-resolved-ids')?.trim() && null,
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes ?? {}, HTMLAttributes), 0];
  },

  addCommands() {
    return {
      setComment:
        (comment: string) =>
        ({ state, commands, tr }) => {
          const { selection, schema } = state;
          let { from, to } = selection;
          if (from === to) {
            const { yjsFrom, yjsTo } = (state as any).preCommentHighlight$;
            const YState = ySyncPluginKey.getState(state);
            from =
              relativePositionToAbsolutePosition(
                YState.doc,
                YState.type,
                yjsFrom,
                YState.binding.mapping
              ) ?? 0;
            to =
              relativePositionToAbsolutePosition(
                YState.doc,
                YState.type,
                yjsTo,
                YState.binding.mapping
              ) ?? 0;
            tr.setSelection(new TextSelection(state.doc.resolve(from), state.doc.resolve(to)));
          }
          // check if there is a comment mark in range
          const rangeHasComments = state.doc.rangeHasMark(from, to, schema.marks.comment);
          if (rangeHasComments) {
            // get all comment nodes in range
            const nodesInRange: {
              comments: string;
              resolved: string;
              nodeStart: number;
              nodeEnd: number;
            }[] = [];

            state.doc.nodesBetween(from, to, (nodeInRange, pos) => {
              const commentMark = getCommentMark(nodeInRange);
              nodesInRange.push({
                comments: commentMark?.attrs?.comment ?? null,
                resolved: commentMark?.attrs?.resolved ?? null,
                nodeStart: pos,
                nodeEnd: pos + nodeInRange.nodeSize,
              });
            });

            // for each of the nodes in range add a new comment mark with existing comment value
            nodesInRange.forEach(
              (
                {
                  nodeStart,
                  nodeEnd,
                  comments,
                  resolved,
                }: { nodeStart: number; nodeEnd: number; comments: string; resolved: string },
                index: number
              ) => {
                const combinedComments = [comment, comments].filter(Boolean).join(' ');
                const singleCommentMark = createCommentMark(comment, schema, resolved);
                const combinedCommentsMark = createCommentMark(combinedComments, schema, resolved);
                const nodeStartWithinSelection = from <= nodeStart;

                // for first comment check if there is space from beginining of selection to node
                if (index === 0) {
                  if (nodeStartWithinSelection) {
                    tr.addMark(from, nodeStart, singleCommentMark);
                  } else {
                    const endPos = nodeEnd <= to ? nodeEnd : to;
                    tr.addMark(from, endPos, combinedCommentsMark);
                  }
                } else {
                  // check if there is a space between node start and previous node end
                  const { nodeEnd: previousNodeEnd } = nodesInRange[index - 1];
                  if (nodeStart - previousNodeEnd > 1) {
                    // add single comment mark for empty space between two comment nodes
                    tr.addMark(previousNodeEnd, nodeStart, singleCommentMark);
                  }
                }
                // check if node ends before selection ends
                const startPos = nodeStartWithinSelection ? nodeStart : from;
                const endPos = nodeEnd <= to ? nodeEnd : to;
                tr.addMark(startPos, endPos, combinedCommentsMark);

                // for last comment check if there is space from node end to selection end
                if (index === nodesInRange.length - 1 && nodeEnd < to) {
                  tr.addMark(nodeEnd, to, singleCommentMark);
                }
              }
            );
            return true;
          }
          // when there are no comments in range
          return commands.setMark(commentMarkName, { comment });
        },
      removeComment:
        (commentId: string) =>
        ({ state, tr }: { state: any; tr: any }): boolean => {
          const { schema } = state;
          const commentMark = schema.marks.comment;

          // find all comment marks in document
          const commentMarks: any = findAllCommentMarks(state.doc);

          // if there are no comment marks in document return false
          if (!commentMarks.length) return false;
          const updateCommmentMark = (commentIds: [], resolved: any, mark: any) => {
            const newCommentIds = commentIds.filter((id: string) => id !== commentId).join(' ');
            if (!newCommentIds && !resolved) {
              // if there are no commentIds left remove the comment mark
              tr.removeMark(mark.from, mark.to, commentMark);
              return;
            }
            // otherwise create a new comment mark with the remaining commentIds
            const newCommentMark = createCommentMark(newCommentIds, schema, resolved);
            tr.addMark(mark.from, mark.to, newCommentMark);
          };

          const updateResolvedCommentMark = (resolvedIds: [], comment: any, mark: any) => {
            const newResolvedIds = resolvedIds.filter((id: string) => id !== commentId).join(' ');
            if (!newResolvedIds && !comment) {
              // if there are no resolvedIds left remove the comment mark
              tr.removeMark(mark.from, mark.to, commentMark);
              return;
            }
            // otherwise create a new comment mark with the remaining resolvedIds
            const newCommentMark = createCommentMark(comment, schema, newResolvedIds);
            tr.addMark(mark.from, mark.to, newCommentMark);
          };

          // for each comment mark check if it contains the commentId
          commentMarks.forEach((mark: any) => {
            const { comment, resolved } = mark.attrs;
            const commentIds = comment?.split(' ');
            const resolvedIds = resolved?.split(' ');
            if (commentIds?.includes(commentId)) {
              // if commentId is found remove it from comment mark
              updateCommmentMark(commentIds, resolved, mark);
            } else if (resolvedIds?.includes(commentId)) {
              // if commentId is not found check if it is in the resolvedIds
              updateResolvedCommentMark(resolvedIds, comment, mark);
            }
          });
          return true;
        },
      resolveComment: (commentId: string) => updateComment(commentId, true),
      unresolveComment: (commentId: string) => updateComment(commentId, false),
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey(commentMarkName),
        props: {
          handleClickOn: (view, pos, node, nodePos, event) => {
            if (this.storage.inlineCommentActive) {
              return true;
            }

            const nodeAtPosition = view.state.doc.nodeAt(pos);
            const commentMark = getCommentMark(nodeAtPosition);

            if (commentMark) {
              const commentIds = commentMark?.attrs?.comment ?? [];
              const [commentId = ''] = commentIds?.split?.(' ') ?? [];
              if (!commentId) return false;
              let element = view.nodeDOM(nodePos);

              const { node: domAtPos } = view.domAtPos(pos);

              if (element?.nodeName !== 'SPAN') {
                if (domAtPos.nodeName === '#text') {
                  element = domAtPos.parentElement;
                } else {
                  const textNode = this.options.getTextNodeFromPoint?.(
                    event.clientX,
                    event.clientY
                  );
                  element = textNode?.parentElement;
                }
              }

              if (this.storage.activeCommentId === commentId) {
                this.storage.activeCommentId = null;
                return false;
              }
              this.storage.activeCommentId = commentId;
              const editor = this.editor as any;

              this.options.emitter?.$emit?.('inline-comment-click', {
                node: element,
                commentId,
                editorId: editor.id,
              });
              return true;
            }
            return false;
          },

          handleDOMEvents: {
            cut: () => {
              this.storage.cut = true;
            },
          },

          transformPasted: (slice) => {
            const { content } = slice;
            const internalDrag = this.editor.storage.MouseOver.dragging;

            if (this.storage.cut) {
              this.storage.cut = false;
              return slice;
            }
            if (internalDrag) {
              this.editor.storage.MouseOver.dragging = false;
              return slice;
            }

            const hasCommentMark = hasCommentMarkInFragment(slice.content);

            if (!hasCommentMark) {
              return slice;
            }

            // Remove all marks from the pasted content
            const transformedContent: any = removeMarks(content);

            // Create a new slice with the transformed content
            return new Slice(transformedContent, slice.openStart, slice.openEnd);
          },
        },
      }),
    ];
  },
});
